import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Package, TrendingUp, AlertCircle, Loader, Activity } from 'lucide-react';
import { barcodeService, recordService, userService } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalBarcodes: 0,
    activatedBarcodes: 0,
    deactivatedBarcodes: 0,
    totalUsers: 0,
    pendingRecords: 0,
    completedRecords: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [barcodeStats, recordStats, userStats] = await Promise.all([
        barcodeService.getStatistics(),
        recordService.getStatistics(),
        userService.getAll(),
      ]);

      setStats({
        totalBarcodes: barcodeStats.data?.data?.total || 0,
        activatedBarcodes: barcodeStats.data?.data?.activated || 0,
        deactivatedBarcodes: barcodeStats.data?.data?.deactivated || 0,
        totalUsers: userStats.data?.data?.users?.length || 0,
        pendingRecords: recordStats.data?.data?.pending || 0,
        completedRecords: recordStats.data?.data?.completed || 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      setStats((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load statistics',
      }));
    }
  };

  const statCards = [
    {
      label: 'Total Barcodes',
      value: stats.totalBarcodes,
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
    },
    {
      label: 'Activated',
      value: stats.activatedBarcodes,
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
    },
    {
      label: 'Deactivated',
      value: stats.deactivatedBarcodes,
      icon: AlertCircle,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
    },
    {
      label: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
  ];

  if (stats.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-4">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-slate-700 font-semibold">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-4xl font-bold text-slate-900 tracking-tight">Admin Dashboard</h2>
        <p className="text-slate-500 mt-2 text-sm font-medium">Monitor system performance and manage resources</p>
      </div>

      {/* Error Message */}
      {stats.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800">{stats.error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div
              key={idx}
              className="bg-white rounded-xl border border-slate-100 p-5 hover:border-slate-200 hover:shadow-sm transition-all duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">{card.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-3">{card.value.toLocaleString()}</p>
                </div>
                <div className={`bg-gradient-to-br ${card.color} p-2.5 rounded-lg opacity-90 group-hover:opacity-100 transition-opacity`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Records Summary */}
        <div className="bg-white rounded-xl border border-slate-100 p-6 hover:border-slate-200 hover:shadow-sm transition-all duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Records Summary</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
              <span className="text-slate-700 font-medium text-sm">Pending Records</span>
              <span className="text-xl font-bold text-blue-600">{stats.pendingRecords}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
              <span className="text-slate-700 font-medium text-sm">Completed Records</span>
              <span className="text-xl font-bold text-green-600">{stats.completedRecords}</span>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-xl border border-slate-100 p-6 hover:border-slate-200 hover:shadow-sm transition-all duration-300">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">System Health</h3>
          </div>
          <div className="space-y-4">
            {[
              { label: 'API Status', status: 'healthy' },
              { label: 'Database', status: 'healthy' },
              { label: 'Cache System', status: 'healthy' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-slate-600 font-medium text-sm">{item.label}</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.status === 'healthy' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  <span className={`text-xs font-semibold ${item.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                    {item.status === 'healthy' ? 'Healthy' : 'Down'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activation Rate */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 hover:border-slate-200 hover:shadow-sm transition-all duration-300">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Barcode Activation Rate</h3>
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <div className="relative h-40 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg overflow-hidden border border-slate-200">
              {/* Activated Bar */}
              <div 
                className="absolute bottom-0 left-0 w-1/2 bg-gradient-to-t from-green-500 to-emerald-400 transition-all duration-500"
                style={{ height: `${stats.totalBarcodes ? (stats.activatedBarcodes / stats.totalBarcodes * 100) : 0}%` }}
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Activation Rate</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {stats.totalBarcodes ? Math.round((stats.activatedBarcodes / stats.totalBarcodes) * 100) : 0}%
              </p>
            </div>
          </div>
          <div className="space-y-3 w-full md:w-auto">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
              <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Activated</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.activatedBarcodes.toLocaleString()}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Deactivated</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.deactivatedBarcodes.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
